/**
 * Priorisation des suggestions — le playbook de l'équipe.
 *
 * L'ordre de cette liste EST le réglage : pendant l'appel, le copilote applique
 * la première règle qui convient à la situation et ne descend que si elle ne
 * colle pas. Mettre « Question directe » en tête ou « La solution en une
 * phrase » change donc la nature des suggestions, sans toucher au code.
 *
 * Une règle qui nomme des déclencheurs ne sort que pour eux, et passe alors
 * devant les règles générales : c'est ce qui fait qu'une objection appelle
 * d'abord une réponse à l'objection, quel que soit le haut du classement.
 */
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import {
  Plus, ChevronUp, ChevronDown, Pencil, Trash2, X, Power, RefreshCw, Hand,
} from "lucide-react";
import api, { apiError } from "@/lib/crmApi";
import Modal from "@/crm/components/Modal";

const EMPTY = {
  label: "", type: "QUESTION", instruction: "",
  triggers: [], objectives: [], sectors: [], manual: true, active: true,
};

export default function SuggestionPlaybook() {
  const [rules, setRules] = useState(null);
  const [catalogue, setCatalogue] = useState({ triggers: [], types: [], objectives: [], sectors: [] });
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);
  const [customSector, setCustomSector] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/copilot/playbook?all=true");
      setRules(data.rules || []);
      setCatalogue(data.catalogue || { triggers: [], types: [], objectives: [], sectors: [] });
    } catch (e) {
      setRules([]);
      toast.error(apiError(e.response?.data?.detail) || "Playbook indisponible");
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  /** Déplace une règle d'un cran et persiste le nouvel ordre. */
  const move = async (index, delta) => {
    const next = [...rules];
    const target = index + delta;
    if (target < 0 || target >= next.length) return;
    [next[index], next[target]] = [next[target], next[index]];
    setRules(next);                                   // optimiste : l'ordre est le réglage
    try { await api.put("/copilot/playbook/order", { ids: next.map((r) => r.id) }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  const toggle = async (rule) => {
    setRules((list) => list.map((r) => (r.id === rule.id ? { ...r, active: !r.active } : r)));
    try { await api.put(`/copilot/playbook/${rule.id}`, { active: !rule.active }); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); load(); }
  };

  const remove = async (rule) => {
    try { await api.delete(`/copilot/playbook/${rule.id}`); toast.success("Règle supprimée"); load(); }
    catch (e) { toast.error(apiError(e.response?.data?.detail)); }
  };

  const save = async () => {
    if (!editing.label.trim()) return toast.error("Donnez un libellé à la règle");
    setSaving(true);
    try {
      if (editing.id) await api.put(`/copilot/playbook/${editing.id}`, editing);
      else await api.post("/copilot/playbook", editing);
      toast.success("Playbook enregistré");
      setEditing(null);
      load();
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  if (rules === null) {
    return (
      <p className="flex items-center justify-center gap-2 py-12 text-sm text-[#8a90a3]">
        <RefreshCw size={14} className="animate-spin" /> Chargement du playbook…
      </p>
    );
  }

  const typeLabel = (code) => catalogue.types.find((t) => t.code === code)?.label || code;
  const triggerLabel = (code) => catalogue.triggers.find((t) => t.code === code)?.label || code;

  return (
    <div data-testid="suggestion-playbook">
      <div className="mb-[18px] flex flex-wrap items-center justify-between gap-3">
        <p className="max-w-[70ch] text-[13.5px] text-[#61667a] text-pretty">
          Pendant l'appel, le copilote applique la première règle applicable de cette liste.
          L'ordre est la priorité : glissez vers le haut ce que vous voulez entendre en premier.
        </p>
        <button data-testid="new-rule-btn" onClick={() => setEditing({ ...EMPTY })}
          className="flex items-center gap-2 rounded-[11px] bg-[#4a3ae0] px-[18px] py-[11px] text-sm font-medium text-white hover:bg-[#3a2cc8]">
          <Plus size={16} /> Nouvelle règle
        </button>
      </div>

      <div className="space-y-2.5">
        {rules.map((rule, i) => (
          <div key={rule.id} data-testid="playbook-rule"
            className={`flex items-start gap-3 rounded-2xl border bg-white p-3.5 ${rule.active ? "border-[#e6e8ee]" : "border-[#eef0f4] opacity-60"}`}>
            <div className="flex flex-col items-center gap-0.5">
              <button onClick={() => move(i, -1)} disabled={i === 0} title="Monter"
                className="rounded-md p-0.5 text-[#b3b8c7] hover:bg-[#f1f2f7] hover:text-[#4c5164] disabled:opacity-30">
                <ChevronUp size={15} />
              </button>
              <span className="font-mono text-[11.5px] text-[#8a90a3]">{i + 1}</span>
              <button onClick={() => move(i, 1)} disabled={i === rules.length - 1} title="Descendre"
                className="rounded-md p-0.5 text-[#b3b8c7] hover:bg-[#f1f2f7] hover:text-[#4c5164] disabled:opacity-30">
                <ChevronDown size={15} />
              </button>
            </div>

            <div className="min-w-0 flex-1">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[14px] font-medium text-[#16181d]">{rule.label}</span>
                <span className="rounded-md bg-[#eeecff] px-1.5 py-0.5 font-mono text-[10.5px] uppercase text-[#4a3ae0]">
                  {typeLabel(rule.type)}
                </span>
                {rule.manual && (
                  <span className="flex items-center gap-1 rounded-md bg-[#f1f2f7] px-1.5 py-0.5 text-[11px] text-[#61667a]">
                    <Hand size={11} /> menu Suggérer
                  </span>
                )}
              </div>
              {rule.instruction && (
                <p className="mt-1 text-[13px] text-[#61667a] text-pretty">{rule.instruction}</p>
              )}
              <div className="mt-1.5 flex flex-wrap gap-1.5">
                {(rule.triggers || []).length === 0 ? (
                  <span className="text-[11.5px] text-[#9aa0b2]">Toutes situations</span>
                ) : (rule.triggers || []).map((t) => (
                  <span key={t} className="rounded-full bg-[#f7f8fa] px-2 py-0.5 text-[11.5px] text-[#61667a]">
                    {triggerLabel(t)}
                  </span>
                ))}
                {(rule.objectives || []).map((o) => (
                  <span key={o} className="rounded-full bg-[#e6f1fb] px-2 py-0.5 text-[11.5px] text-[#1f5f9e]">
                    objectif : {o}
                  </span>
                ))}
                {(rule.sectors || []).map((s) => (
                  <span key={s} className="rounded-full bg-[#e6f5ec] px-2 py-0.5 text-[11.5px] text-[#1f7a48]">
                    secteur : {s}
                  </span>
                ))}
              </div>
            </div>

            <div className="flex shrink-0 gap-1.5">
              <button onClick={() => toggle(rule)} title={rule.active ? "Désactiver" : "Activer"}
                className={`rounded-lg border p-1.5 ${rule.active ? "border-[#cfe9d9] text-[#2f9e5c]" : "border-[#e2e5ec] text-[#b3b8c7]"}`}>
                <Power size={14} />
              </button>
              <button onClick={() => setEditing({ ...rule })} title="Modifier"
                className="rounded-lg border border-[#e2e5ec] p-1.5 text-[#4c5164] hover:border-[#c9cedb]">
                <Pencil size={14} />
              </button>
              <button onClick={() => remove(rule)} title="Supprimer"
                className="rounded-lg border border-[#fbdcd8] p-1.5 text-[#d64a3a] hover:bg-[#fdeceb]">
                <Trash2 size={14} />
              </button>
            </div>
          </div>
        ))}
      </div>

      {editing && (
        <Modal onClose={() => setEditing(null)} className="max-w-[560px]" testid="rule-editor"
          label={editing.id ? "Modifier la règle" : "Nouvelle règle"}>
          <div className="flex items-start justify-between">
            <h3 className="font-heading text-[17px] font-bold">
              {editing.id ? "Modifier la règle" : "Nouvelle règle"}
            </h3>
            <button onClick={() => setEditing(null)} className="text-slate-400 hover:text-slate-900">
              <X size={18} />
            </button>
          </div>

          <label className="mt-4 block text-[13px] font-medium text-[#4c5164]">
            Libellé — ce que verra le commercial
            <input data-testid="rule-label" value={editing.label}
              onChange={(e) => setEditing({ ...editing, label: e.target.value })}
              placeholder="Question directe"
              className="mt-1.5 w-full rounded-xl border border-[#e2e5ec] px-3 py-2.5 text-sm outline-none focus:border-[#5b4bff]" />
          </label>

          <label className="mt-3 block text-[13px] font-medium text-[#4c5164]">
            Type de suggestion
            <select value={editing.type} onChange={(e) => setEditing({ ...editing, type: e.target.value })}
              className="mt-1.5 w-full rounded-xl border border-[#e2e5ec] bg-white px-3 py-2.5 text-sm">
              {catalogue.types.map((t) => <option key={t.code} value={t.code}>{t.label}</option>)}
            </select>
          </label>

          <label className="mt-3 block text-[13px] font-medium text-[#4c5164]">
            Consigne donnée à l'IA
            <textarea data-testid="rule-instruction" value={editing.instruction} rows={4}
              onChange={(e) => setEditing({ ...editing, instruction: e.target.value })}
              placeholder="Pose UNE question directe et fermée qui fait avancer la qualification."
              className="mt-1.5 w-full rounded-xl border border-[#e2e5ec] p-3 text-sm outline-none focus:border-[#5b4bff]" />
          </label>

          <fieldset className="mt-3">
            <legend className="text-[13px] font-medium text-[#4c5164]">
              Situations concernées <span className="text-[#9aa0b2]">— aucune cochée = toutes</span>
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {catalogue.triggers.map((t) => {
                const on = (editing.triggers || []).includes(t.code);
                return (
                  <button key={t.code} type="button"
                    onClick={() => setEditing({
                      ...editing,
                      triggers: on ? editing.triggers.filter((x) => x !== t.code)
                                   : [...(editing.triggers || []), t.code],
                    })}
                    className={`rounded-full border px-2.5 py-1 text-[12.5px] ${on ? "border-[#b9b1ff] bg-[#eeecff] text-[#3a2cc8]" : "border-[#e2e5ec] text-[#61667a] hover:border-[#c9cedb]"}`}>
                    {t.label}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="mt-3">
            <legend className="text-[13px] font-medium text-[#4c5164]">
              Objectifs d'appel <span className="text-[#9aa0b2]">— aucun coché = tous</span>
            </legend>
            <div className="mt-1.5 flex flex-wrap gap-1.5">
              {catalogue.objectives.map((o) => {
                const on = (editing.objectives || []).includes(o);
                return (
                  <button key={o} type="button"
                    onClick={() => setEditing({
                      ...editing,
                      objectives: on ? editing.objectives.filter((x) => x !== o)
                                     : [...(editing.objectives || []), o],
                    })}
                    className={`rounded-full border px-2.5 py-1 text-[12.5px] ${on ? "border-[#9fc7ea] bg-[#e6f1fb] text-[#1f5f9e]" : "border-[#e2e5ec] text-[#61667a] hover:border-[#c9cedb]"}`}>
                    {o}
                  </button>
                );
              })}
            </div>
          </fieldset>

          <fieldset className="mt-3">
            <legend className="text-[13px] font-medium text-[#4c5164]">
              Secteurs concernés <span className="text-[#9aa0b2]">— aucun coché = tous ; la règle
              ne sort que si l'activité de la fiche contient le mot-clé</span>
            </legend>
            <div className="mt-1.5 flex flex-wrap items-center gap-1.5">
              {[...new Set([...(catalogue.sectors || []), ...(editing.sectors || [])])].map((s) => {
                const on = (editing.sectors || []).includes(s);
                return (
                  <button key={s} type="button"
                    onClick={() => setEditing({
                      ...editing,
                      sectors: on ? editing.sectors.filter((x) => x !== s)
                                  : [...(editing.sectors || []), s],
                    })}
                    className={`rounded-full border px-2.5 py-1 text-[12.5px] ${on ? "border-[#9fd4b3] bg-[#e6f5ec] text-[#1f7a48]" : "border-[#e2e5ec] text-[#61667a] hover:border-[#c9cedb]"}`}>
                    {s}
                  </button>
                );
              })}
              <input value={customSector} onChange={(e) => setCustomSector(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key !== "Enter") return;
                  e.preventDefault();
                  const mot = customSector.trim().toLowerCase();
                  if (mot && !(editing.sectors || []).includes(mot)) {
                    setEditing({ ...editing, sectors: [...(editing.sectors || []), mot] });
                  }
                  setCustomSector("");
                }}
                placeholder="autre mot-clé + Entrée"
                className="w-[160px] rounded-full border border-dashed border-[#e2e5ec] px-2.5 py-1 text-[12.5px] outline-none focus:border-[#5b4bff]" />
            </div>
          </fieldset>

          <label className="mt-3 flex cursor-pointer items-center gap-2 text-[13.5px] text-[#4c5164]">
            <input type="checkbox" checked={Boolean(editing.manual)}
              onChange={(e) => setEditing({ ...editing, manual: e.target.checked })} />
            Proposer cette règle dans le menu « Suggérer » du panneau d'appel
          </label>

          <div className="mt-5 flex gap-2">
            <button onClick={() => setEditing(null)}
              className="flex-1 rounded-xl border border-[#e2e5ec] py-2.5 text-sm text-[#4c5164] hover:border-[#c9cedb]">
              Annuler
            </button>
            <button data-testid="save-rule" onClick={save} disabled={saving}
              className="flex-1 rounded-xl bg-[#4a3ae0] py-2.5 text-sm font-medium text-white hover:bg-[#3a2cc8] disabled:opacity-50">
              Enregistrer
            </button>
          </div>
        </Modal>
      )}
    </div>
  );
}
